"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase";
import { resetAnalyticsIdentity } from "@/lib/analytics";
import {
  signOutContinuePath,
  type SignOutContinueTarget,
} from "@/lib/account/personal-account-hiring";

/**
 * Signs the current (personal) account out and continues to an employer
 * route. Mirrors the navbar logout: clears offline and widget state, signs
 * out, then uses a hard navigation so server-side cookie state is cleared.
 */
export function SignOutAndContinueButton({
  target,
  label,
  variant = "primary",
  continueParams,
}: {
  target: SignOutContinueTarget;
  label: string;
  variant?: "primary" | "secondary";
  /** Invite, source and UTM parameters carried to employer signup. */
  continueParams?: Record<string, string>;
}) {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function signOutAndContinue() {
    if (pending) return;
    setPending(true);
    setFailed(false);
    try {
      const { clearOfflineAccount } = await import("@/lib/offline/context");
      await clearOfflineAccount();
      const { clearNativeWidgetSnapshot } = await import("@/lib/native/widget-bridge");
      await clearNativeWidgetSnapshot();
      const { error } = await createClient().auth.signOut();
      if (error) throw error;
      resetAnalyticsIdentity();
      window.location.href = signOutContinuePath(target, continueParams);
    } catch {
      setFailed(true);
      setPending(false);
    }
  }

  const className =
    variant === "primary"
      ? "inline-flex min-h-[44px] w-full items-center justify-center rounded-sm bg-teal px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-teal-dark disabled:opacity-60"
      : "inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border-[1.5px] border-navy/20 bg-transparent px-5 py-3 text-[14px] font-semibold text-navy transition-colors hover:border-teal hover:text-teal disabled:opacity-60";

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => { void signOutAndContinue(); }}
        disabled={pending}
        className={className}
      >
        {pending ? "Signing out..." : label}
      </button>
      {failed ? (
        <p role="alert" className="mt-2 text-[13px] text-error">
          We could not sign you out. Please try again, or log out from the menu.
        </p>
      ) : null}
    </div>
  );
}
